import { create } from 'zustand';
import { CHAT_KINDS, CHAT_PRICE_BANDS, CHAT_RADII_M } from '@citiguide/shared';
import type { ChatRadiusM, CuratedState } from '@citiguide/shared';
import { ID_PATTERN, MAX_PINS, MAX_PLAN_NAME } from '../utils/urlParams';
import { savedPlansKey } from '../utils/storageScope';

/** Saved plans live in localStorage per subdomain (Decision roFovcfu); no accounts, no server. */
export const MAX_SAVED_PLANS = 50;

export interface SavedPlan {
  id: string;
  name: string;
  state: CuratedState;
  pinnedMarkerIds: string[];
  createdAt: string;
}

const idList = (value: unknown, max: number): string[] =>
  Array.isArray(value)
    ? [...new Set(value.filter((v): v is string => typeof v === 'string' && ID_PATTERN.test(v)))].slice(0, max)
    : [];

const sanitizeState = (value: unknown): CuratedState | null => {
  const v = value as Partial<CuratedState> | null;
  if (!v || typeof v !== 'object') return null;
  const radius = (v.near as { radius_m?: unknown } | null | undefined)?.radius_m;
  return {
    categories: idList(v.categories, 12),
    audience: idList(v.audience, 6),
    price: (CHAT_PRICE_BANDS as readonly string[]).includes(v.price as string) ? v.price! : null,
    kind: (CHAT_KINDS as readonly string[]).includes(v.kind as string) ? v.kind! : null,
    near: (CHAT_RADII_M as readonly number[]).includes(radius as number)
      ? { radius_m: radius as ChatRadiusM }
      : null,
  };
};

/** Field-by-field validation of one stored plan; anything invalid is dropped, never thrown. */
export const sanitizePlan = (value: unknown): SavedPlan | null => {
  const p = value as Partial<SavedPlan> | null;
  if (!p || typeof p !== 'object') return null;
  if (typeof p.id !== 'string' || !p.id || typeof p.name !== 'string' || typeof p.createdAt !== 'string') return null;
  const state = sanitizeState(p.state);
  if (!state) return null;
  const name = p.name.trim().slice(0, MAX_PLAN_NAME);
  if (!name) return null;
  return { id: p.id, name, state, pinnedMarkerIds: idList(p.pinnedMarkerIds, MAX_PINS), createdAt: p.createdAt };
};

export const readPlans = (): SavedPlan[] => {
  try {
    const raw = window.localStorage.getItem(savedPlansKey());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(sanitizePlan).filter((p): p is SavedPlan => p !== null).slice(-MAX_SAVED_PLANS);
  } catch {
    return [];
  }
};

const writePlans = (plans: SavedPlan[]) => {
  try {
    window.localStorage.setItem(savedPlansKey(), JSON.stringify(plans));
  } catch {
    // storage full or blocked: the in-memory list still works for this page load
  }
};

const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `plan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

interface PlansStore {
  plans: SavedPlan[];
  /** My plans modal (shared by the MainMenu button and the chat's "Open My plans"). */
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  save: (plan: { name: string; state: CuratedState; pinnedMarkerIds: string[] }) => SavedPlan | null;
  rename: (id: string, name: string) => void;
  remove: (id: string) => void;
  list: () => SavedPlan[];
}

export const usePlansStore = create<PlansStore>((set, get) => {
  const commit = (plans: SavedPlan[]) => {
    writePlans(plans);
    set({ plans });
  };
  return {
    plans: readPlans(),
    isModalOpen: false,
    openModal: () => set({ isModalOpen: true }),
    closeModal: () => set({ isModalOpen: false }),
    save: (input) => {
      const plan = sanitizePlan({
        id: newId(),
        name: input.name,
        state: input.state,
        pinnedMarkerIds: input.pinnedMarkerIds,
        createdAt: new Date().toISOString(),
      });
      if (!plan) return null;
      commit([...get().plans, plan].slice(-MAX_SAVED_PLANS));
      return plan;
    },
    rename: (id, name) => {
      const trimmed = name.trim().slice(0, MAX_PLAN_NAME);
      if (!trimmed) return;
      commit(get().plans.map((p) => (p.id === id ? { ...p, name: trimmed } : p)));
    },
    remove: (id) => commit(get().plans.filter((p) => p.id !== id)),
    list: () => get().plans,
  };
});
